// src/pages/Auth/RegisterPage.tsx  (mevcut yolunuza göre güncelleyin)
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { User, Mail, Phone, Lock, Eye, EyeOff, UserPlus } from "lucide-react";
import { useTheme } from "../../../context/ThemeContext";
import { useCustomerAuth } from "../../../context/CustomerAuthContext/CustomerAuthContext";

const isEmail = (v: string) => (v ? /\S+@\S+\.\S+/.test(v) : true);
const isPhone = (v: string) => (v ? /^[0-9 +()-]{7,}$/.test(v) : true);

const RegisterPage: React.FC = () => {
  const { register, loading, token } = useCustomerAuth();
  const { theme } = useTheme();
  const nav = useNavigate();

  // Eğer login ise bu sayfada bekletme → panel
  useEffect(() => { if (token) nav("/musteri/panel", { replace: true }); }, [token, nav]);

  const [ad, setAd] = useState("");
  const [soyad, setSoyad] = useState("");
  const [eposta, setEposta] = useState("");
  const [telefon, setTelefon] = useState("");
  const [sifre, setSifre] = useState("");
  const [show, setShow] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const adErr = useMemo(() => (touched.ad && !ad.trim() ? "Ad zorunludur." : ""), [ad, touched.ad]);
  const soyadErr = useMemo(() => (touched.soyad && !soyad.trim() ? "Soyad zorunludur." : ""), [soyad, touched.soyad]);
  const emailErr = useMemo(() => (touched.eposta && !isEmail(eposta) ? "Geçerli e-posta girin." : ""), [eposta, touched.eposta]);
  const telErr = useMemo(() => (touched.telefon && !isPhone(telefon) ? "Geçerli telefon girin." : ""), [telefon, touched.telefon]);
  const passErr = useMemo(() => (touched.sifre && sifre.length < 6 ? "Şifre en az 6 karakter olmalı." : ""), [sifre, touched.sifre]);
  const oneErr = useMemo(() => (touched._one && !eposta.trim() && !telefon.trim() ? "E-posta veya Telefon zorunlu." : ""), [eposta, telefon, touched._one]);

  const passScore = useMemo(() => {
    let s = 0;
    if (sifre.length >= 6) s++;
    if (/[A-Z]/.test(sifre)) s++;
    if (/[a-z]/.test(sifre)) s++;
    if (/\d/.test(sifre)) s++;
    if (/[^A-Za-z0-9]/.test(sifre)) s++;
    return s;
  }, [sifre]);

  const doRegister = async () => {
    setTouched({ ad: true, soyad: true, eposta: true, telefon: true, sifre: true, _one: true });
    setErr(null);
    if (adErr || soyadErr || emailErr || telErr || passErr || oneErr) return;

    const res = await register({
      Ad: ad.trim(),
      Soyad: soyad.trim(),
      Eposta: eposta.trim() || undefined,
      Telefon: telefon.trim() || undefined,
      Sifre: sifre,
    });

    if (res.success) nav("/musteri/panel", { replace: true });
    else {
      const msg =
        res.errorDetail ||
        (res.errorCode === "DUPLICATE" && "Bu e-posta/telefon ile kayıt zaten var.") ||
        (res.errorCode === "VALIDATION" && "Zorunlu alanları kontrol edin.") ||
        "Kayıt başarısız.";
      setErr(String(msg));
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => { if (e.key === "Enter") doRegister(); };

  // ===== Theme-aware surfaces & colors =====
  const hexToRgb = (hex: string) => {
    const h = (hex || "#0f1115").replace("#", "");
    const full = h.length === 3 ? h.split("").map(c => c + c).join("") : h;
    const n = parseInt(full, 16);
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
  };
  const luminance = ({ r, g, b }: { r: number; g: number; b: number }) => {
    const f = (v: number) => {
      v /= 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
  };

  const bgHex = theme?.backgroundColor || "#0f1115";
  const isDark = luminance(hexToRgb(bgHex)) < 0.5;

  const surface1 = isDark ? "rgba(255,255,255,.035)" : "rgba(0,0,0,.035)";
  const surface2 = isDark ? "rgba(255,255,255,.06)"  : "rgba(0,0,0,.06)";
  const divider  = isDark ? "rgba(255,255,255,.10)" : "rgba(0,0,0,.12)";
  const softShadow = isDark ? "0 10px 28px rgba(0,0,0,.28)" : "0 10px 28px rgba(0,0,0,.10)";

  const accent = theme?.accent ?? "#22c55e";
  const textPrimary = theme?.textPrimary ?? (isDark ? "#fff" : "#111827");
  const textSecondary = theme?.textSecondary ?? (isDark ? "rgba(255,255,255,.7)" : "#6b7280");
  const gradient = theme?.gradientHero ?? "linear-gradient(90deg,#0ea5e9,#22c55e)";

  const strengthText = ["Zayıf", "Zayıf", "Orta", "İyi", "Güçlü", "Çok Güçlü"][passScore];
  const strengthBar = ["#ef4444", "#ef4444", "#eab308", "#84cc16", "#10b981", "#059669"][passScore];

  return (
    <div style={{ backgroundColor: theme?.backgroundColor }}>
      <div className="min-h-[70vh] grid place-items-center px-4 py-10">
        <div className="w-full max-w-2xl" onKeyDown={onKeyDown}>
          {/* HEADER / HERO */}
          <div className="rounded-t-2xl p-[1px]" style={{ background: gradient }}>
            <div className="rounded-t-2xl p-6 backdrop-blur"
                 style={{ background: isDark ? "rgba(0,0,0,.28)" : "rgba(255,255,255,.7)" }}>
              <h1 className="text-2xl font-semibold" style={{ color: "#fff" }}>Kayıt Ol</h1>
              <p className="text-sm mt-1" style={{ color: "rgba(255,255,255,.9)" }}>
                Hızlıca bir hesap oluşturun, sipariş ve adreslerinizi yönetin.
              </p>
            </div>
          </div>

          {/* FORM PANEL */}
          <div className="rounded-b-2xl p-6 border"
               style={{ backgroundColor: surface1, borderColor: divider, boxShadow: softShadow }}>
            <div className="grid md:grid-cols-2 gap-4">
              {/* Ad */}
              <div>
                <label className="block text-sm mb-1" style={{ color: textPrimary }}>
                  Ad <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <div
                  className="flex items-center gap-2 rounded-lg px-3 py-2 border"
                  style={{ borderColor: adErr ? "#ef4444" : divider, backgroundColor: surface2 }}
                >
                  <User size={18} style={{ color: textSecondary }} />
                  <input
                    value={ad}
                    onChange={(e) => setAd(e.target.value)}
                    onBlur={() => setTouched((t) => ({ ...t, ad: true }))}
                    className="w-full outline-none bg-transparent text-sm"
                    style={{ color: textPrimary }}
                    placeholder="Adınız"
                  />
                </div>
                {adErr && <p className="mt-1 text-xs" style={{ color: "#ef4444" }}>{adErr}</p>}
              </div>

              {/* Soyad */}
              <div>
                <label className="block text-sm mb-1" style={{ color: textPrimary }}>
                  Soyad <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <div
                  className="flex items-center gap-2 rounded-lg px-3 py-2 border"
                  style={{ borderColor: soyadErr ? "#ef4444" : divider, backgroundColor: surface2 }}
                >
                  <User size={18} style={{ color: textSecondary }} />
                  <input
                    value={soyad}
                    onChange={(e) => setSoyad(e.target.value)}
                    onBlur={() => setTouched((t) => ({ ...t, soyad: true }))}
                    className="w-full outline-none bg-transparent text-sm"
                    style={{ color: textPrimary }}
                    placeholder="Soyadınız"
                  />
                </div>
                {soyadErr && <p className="mt-1 text-xs" style={{ color: "#ef4444" }}>{soyadErr}</p>}
              </div>

              {/* E-posta */}
              <div>
                <label className="block text-sm mb-1" style={{ color: textPrimary }}>E-posta (opsiyonel)</label>
                <div
                  className="flex items-center gap-2 rounded-lg px-3 py-2 border"
                  style={{ borderColor: emailErr ? "#ef4444" : divider, backgroundColor: surface2 }}
                >
                  <Mail size={18} style={{ color: textSecondary }} />
                  <input
                    value={eposta}
                    onChange={(e) => setEposta(e.target.value)}
                    onBlur={() => setTouched((t) => ({ ...t, eposta: true, _one: true }))}
                    className="w-full outline-none bg-transparent text-sm"
                    style={{ color: textPrimary }}
                    placeholder="ornek@site.com"
                  />
                </div>
                {emailErr && <p className="mt-1 text-xs" style={{ color: "#ef4444" }}>{emailErr}</p>}
              </div>

              {/* Telefon */}
              <div>
                <label className="block text-sm mb-1" style={{ color: textPrimary }}>Telefon (opsiyonel)</label>
                <div
                  className="flex items-center gap-2 rounded-lg px-3 py-2 border"
                  style={{ borderColor: telErr ? "#ef4444" : divider, backgroundColor: surface2 }}
                >
                  <Phone size={18} style={{ color: textSecondary }} />
                  <input
                    value={telefon}
                    onChange={(e) => setTelefon(e.target.value)}
                    onBlur={() => setTouched((t) => ({ ...t, telefon: true, _one: true }))}
                    className="w-full outline-none bg-transparent text-sm"
                    style={{ color: textPrimary }}
                    placeholder="0532xxxxxxx"
                  />
                </div>
                {telErr && <p className="mt-1 text-xs" style={{ color: "#ef4444" }}>{telErr}</p>}
              </div>
            </div>

            {oneErr && (
              <div
                className="mt-2 rounded-md px-3 py-2 text-xs"
                style={{ backgroundColor: isDark ? "rgba(251,191,36,.12)" : "#fffbeb", border: `1px solid ${isDark ? "rgba(251,191,36,.25)" : "#fde68a"}`, color: isDark ? "#fbbf24" : "#92400e" }}
              >
                {oneErr}
              </div>
            )}

            {/* Şifre */}
            <div className="mt-4">
              <label className="block text-sm mb-1" style={{ color: textPrimary }}>
                Şifre <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <div
                className="flex items-center gap-2 rounded-lg px-3 py-2 border"
                style={{ borderColor: passErr ? "#ef4444" : divider, backgroundColor: surface2 }}
              >
                <Lock size={18} style={{ color: textSecondary }} />
                <input
                  type={show ? "text" : "password"}
                  value={sifre}
                  onChange={(e) => setSifre(e.target.value)}
                  onBlur={() => setTouched((t) => ({ ...t, sifre: true }))}
                  placeholder="En az 6 karakter"
                  className="w-full outline-none bg-transparent text-sm"
                  style={{ color: textPrimary }}
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  className="transition-colors"
                  style={{ color: textSecondary }}
                >
                  {show ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {passErr && <p className="mt-1 text-xs" style={{ color: "#ef4444" }}>{passErr}</p>}

              <div className="mt-2">
                <div className="h-1.5 w-full rounded" style={{ backgroundColor: surface2 }}>
                  <div
                    className="h-1.5 rounded"
                    style={{ width: `${(passScore / 5) * 100}%`, background: strengthBar }}
                  />
                </div>
                <p className="text-xs mt-1" style={{ color: textSecondary }}>
                  Şifre gücü: {strengthText}
                </p>
              </div>
            </div>

            {err && (
              <div
                className="mt-3 rounded-md px-3 py-2 text-sm"
                style={{ backgroundColor: isDark ? "rgba(239,68,68,.12)" : "#fef2f2", border: `1px solid ${isDark ? "rgba(239,68,68,.25)" : "#fecaca"}`, color: isDark ? "#f87171" : "#991b1b" }}
              >
                {err}
              </div>
            )}

            <button
              type="button"
              onClick={doRegister}
              disabled={loading}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-lg text-white font-medium h-11 disabled:opacity-60"
              style={{ background: accent }}
            >
              <UserPlus size={18} /> {loading ? "Kaydediliyor..." : "Kayıt Ol"}
            </button>

            <p className="text-center text-sm mt-3" style={{ color: textSecondary }}>
              Zaten hesabın var mı?{" "}
              <Link to="/musteri/giris" className="underline" style={{ color: accent }}>
                Giriş Yap
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
